import { useEffect, useState } from "react";
import { Sliders, X, RotateCcw, Radio, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/audit";
import {
  getGlobalOffset,
  setGlobalOffset,
  setSyncState,
} from "@/lib/sync/globalTvOffsetBus";
import {
  runAudioCalibration,
  type CalibrationState,
  type FingerprintResult,
} from "@/lib/sync/audioFingerprint";

/** Back-compat shim: now delegates to the Global TV Offset Bus. */
export function getManualOffset(): number {
  return getGlobalOffset();
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (deltaSeconds: number) => void;
}

export function ManualOffsetSlider({ open, onClose, onApply }: Props) {
  const [value, setValue] = useState<number>(0);
  const [calibState, setCalibState] = useState<CalibrationState>("idle");
  const [calibResult, setCalibResult] = useState<FingerprintResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(getGlobalOffset());
      void trackEvent("manual_offset_opened", {});
    }
  }, [open]);

  if (!open) return null;

  // PR-37 — TV Sync UX rebuild: positive-only offsets. The app can only
  // delay itself to match a TV that lags behind the live feed; it cannot
  // show the future. This eliminates the persistent "−60s" stuck state.
  const change = (v: number) => {
    const clamped = Math.max(0, Math.min(60, v));
    setValue(clamped);
    void trackEvent("manual_offset_changed", { offset: clamped });
  };

  const save = () => {
    setGlobalOffset(value, "manual");
    setSyncState(value === 0 ? "idle" : "manual_sync");
    onApply(value);
    void trackEvent("manual_offset_saved", { offset: value });
    toast.success("Sincronización manual aplicada", {
      description: `Ajuste: ${value >= 0 ? "+" : ""}${value.toFixed(1)}s`,
    });
    onClose();
  };

  const reset = () => change(0);

  const runCalibration = async () => {
    setCalibResult(null);
    try {
      void trackEvent("audio_calibration_started", {});
      const result = await runAudioCalibration((s) => {
        setCalibState(s);
        // Propagate calibration phases to the global TV Sync bus so the
        // single-surface button reflects real-time progress.
        if (s === "listening") setSyncState("listening");
        if (s === "analyzing") setSyncState("analyzing");
      });
      setCalibResult(result);
      void trackEvent("audio_calibration_done", {
        delay: result.estimatedDelaySec,
        confidence: result.confidence,
        intensity: result.broadcastIntensity,
        commercialLikely: result.commercialLikely,
      });
      if (result.confidence >= 0.4) {
        change(result.estimatedDelaySec);
        setSyncState("synced");
        toast.success("Calibración inteligente lista", {
          description: `Sugerido +${result.estimatedDelaySec}s · ${Math.round(result.confidence * 100)}% confianza`,
        });
      } else {
        setSyncState("low_confidence");
        toast.message("Confianza baja", {
          description: "Intenta en un momento con más sonido de la transmisión.",
        });
      }
    } catch (e) {
      const err = (e as Error).message;
      void trackEvent("audio_calibration_failed", { error: err });
      if (err === "denied") {
        setSyncState("mic_denied");
        toast.error("Micrófono denegado");
      } else if (err === "unsupported") {
        setSyncState("unsupported");
        toast.error("Calibración no soportada en este navegador");
      } else {
        setSyncState("low_confidence");
        toast.error("No se pudo calibrar");
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-background/55 px-3 pb-4 pt-6 backdrop-blur-md sm:items-center sm:p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[oklch(0.18_0.02_270)]/95 to-[oklch(0.08_0.02_270)]/95 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/40 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-3 flex items-center gap-2">
          <Sliders className="h-4 w-4 text-[oklch(0.7_0.18_230)]" />
          <h2 className="text-sm font-black tracking-tight">Sincronización manual</h2>
        </div>
        <p className="mb-4 text-[11px] text-muted-foreground">
          Puedes ajustar manualmente la app con tu transmisión en 2 segundos. Útil si no usas Audio Sync o si hay ruido.
        </p>

        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[10px] font-bold tracking-widest text-muted-foreground">OFFSET</span>
          <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
            Modo actual: {value === 0 ? "Ultra Live" : `Live +${value.toFixed(1)}s`}
          </span>
        </div>

        <div className="mb-3 grid grid-cols-5 gap-1.5 text-[10px] font-bold">
          {[0, 5, 10, 20, 30].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => change(p)}
              className={`rounded-lg px-2 py-1.5 transition ${
                value === p
                  ? "bg-team-overlay-soft text-team-overlay border border-team-overlay shadow"
                  : "glass hover:bg-accent"
              }`}
            >
              {p === 0 ? "Live" : `+${p}s`}
            </button>
          ))}
        </div>

        <input
          type="range"
          min={0}
          max={60}
          step={0.5}
          value={value}
          onChange={(e) => change(Number(e.target.value))}
          aria-label="Offset en segundos"
          className="w-full"
          style={{ accentColor: "var(--team-overlay-accent, var(--team-primary, oklch(0.7 0.22 27)))" }}
        />
        <div className="mt-1 flex justify-between text-[9px] text-muted-foreground tabular-nums">
          <span>Live</span><span>+30s</span><span>+60s</span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] font-bold">
          <button
            onClick={() => change(value - 1)}
            className="rounded-xl glass px-2 py-2 hover:bg-accent"
          >
            Menos retraso
            <div className="text-[9px] font-semibold text-muted-foreground">−1.0s</div>
          </button>
          <button
            onClick={reset}
            className="rounded-xl glass px-2 py-2 hover:bg-accent flex items-center justify-center gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Ultra Live
          </button>
          <button
            onClick={() => change(value + 1)}
            className="rounded-xl glass px-2 py-2 hover:bg-accent"
          >
            Más retraso
            <div className="text-[9px] font-semibold text-muted-foreground">+1.0s</div>
          </button>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl px-3 py-2 text-xs font-bold text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            className="rounded-xl bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] px-4 py-2 text-xs font-black text-white shadow-lg hover:scale-[1.02]"
          >
            Guardar offset
          </button>
        </div>

        {/* Audio Sync Intelligence — advanced calibration. No duplicate button. */}
        <div className="mt-5 border-t border-white/10 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="flex items-center gap-2 text-[11px] font-black tracking-wide text-[oklch(0.78_0.18_230)]">
              <Sparkles className="h-3.5 w-3.5" />
              Sincronización inteligente
            </span>
            <span className="text-[10px] font-bold text-muted-foreground">
              {showAdvanced ? "Ocultar" : "Mostrar"}
            </span>
          </button>

          {showAdvanced && (
            <div className="mt-3 space-y-3 animate-fade-in">
              <p className="text-[10px] leading-snug text-muted-foreground">
                Calibración acústica temporal (≈2.5s). Detecta el delay real de tu transmisión
                escuchando un instante del partido.
              </p>

              <button
                type="button"
                onClick={runCalibration}
                disabled={
                  calibState === "requesting_mic" ||
                  calibState === "listening" ||
                  calibState === "analyzing"
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[oklch(0.7_0.18_230)]/40 bg-[oklch(0.7_0.18_230)]/10 px-3 py-2.5 text-[11px] font-black text-[oklch(0.85_0.15_230)] transition hover:bg-[oklch(0.7_0.18_230)]/20 disabled:opacity-60"
              >
                <Radio className="h-3.5 w-3.5" />
                {calibState === "requesting_mic" && "Solicitando micrófono…"}
                {calibState === "listening" && "Escuchando…"}
                {calibState === "analyzing" && "Analizando huella acústica…"}
                {(calibState === "idle" || calibState === "done" || calibState === "denied" || calibState === "error" || calibState === "unsupported") &&
                  "Iniciar calibración acústica"}
              </button>

              {calibResult && (
                <div className="rounded-xl border border-white/10 bg-background/40 p-3 text-[10px]">
                  <div className="mb-1 flex justify-between font-bold">
                    <span>Delay estimado</span>
                    <span className="tabular-nums text-[oklch(0.85_0.15_230)]">
                      +{calibResult.estimatedDelaySec}s
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Confianza</span>
                    <span className="tabular-nums">{Math.round(calibResult.confidence * 100)}%</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Intensidad broadcast</span>
                    <span className="capitalize">{calibResult.broadcastIntensity}</span>
                  </div>
                  {calibResult.commercialLikely && (
                    <div className="mt-1 text-[oklch(0.78_0.18_60)]">Ventana sponsor detectada</div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-1.5 rounded-lg bg-white/5 p-2 text-[9.5px] leading-snug text-muted-foreground">
                <ShieldCheck className="mt-px h-3 w-3 shrink-0 text-[oklch(0.78_0.18_145)]" />
                <div>
                  No almacenamos audio · Calibración temporal · Protección anti-spoilers
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}